import React, { useEffect, useState } from "react";
import { Text } from "@mantine/core";
import { useModals } from "@mantine/modals";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Table,
} from "react-bootstrap";
import { BiTrash } from "react-icons/bi";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  deleteSchoolPerformance,
  editSchoolPerformance,
  getBatchSchoolPerformanceResult,
} from "../../actions/SchoolPerformance.action";
import { years } from "../../constants/MonthsAndYears";
import { toast } from "react-toastify";
import {
  getTermCountOptions,
  getTermLabel,
  getTermOptions,
} from "../../utils/coachingPerformance";
import styles from "../EditCoachingPerformanceForm/EditCoachingPerformanceForm.module.css";

const isValidTotalMark = (value) => {
  const parsedValue = parseFloat(value);
  return Number.isFinite(parsedValue) && parsedValue > 0;
};

const isValidGainedMark = (value) => {
  const parsedValue = parseFloat(value);
  return Number.isFinite(parsedValue) && parsedValue >= 0;
};

const EditSchoolPerformanceForm = ({
  grades,
  getBatchSchoolPerformanceResult,
  editSchoolPerformance,
  deleteSchoolPerformance,
  extra,
}) => {
  const [grade, setGrade] = useState("");
  const [batch, setBatch] = useState("");
  const [shift, setShift] = useState("");
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [termCount, setTermCount] = useState("");
  const [term, setTerm] = useState("");
  const [students, setStudents] = useState(null);

  const navigate = useNavigate();
  const modals = useModals();

  const updateStudentField = (studentId, field, value) => {
    setStudents(
      students.map((student) =>
        student.id === studentId
          ? {
              ...student,
              [field]: value,
            }
          : student
      )
    );
  };

  const selectHandeler = async () => {
    if (!term) {
      toast.error("Please select term type first!");
      return;
    }
    const list = await getBatchSchoolPerformanceResult(year, term, batch);
    if (!list || list.length === 0) {
      setStudents([]);
      return;
    }

    if (list.length > 0 && list[0].term_count) {
      setTermCount(list[0].term_count.toString());
    }

    setStudents(
      list.map((l) => ({
        id: l.schoolPerformances.length > 0 ? l.schoolPerformances[0].id : 0,
        studentId: l.id,
        uid: l.uid,
        name: l.name,
        phone: l.phone,
        term_count: l.term_count,
        gained:
          l.schoolPerformances.length > 0
            ? l.schoolPerformances[0].gained.toString()
            : "0",
        total:
          l.schoolPerformances.length > 0
            ? l.schoolPerformances[0].total.toString()
            : "0",
      }))
    );

    if (list.length > 0 && list[0].schoolPerformances.length > 0) {
      setTerm(list[0].schoolPerformances[0].term.toString());
    }
  };

  useEffect(() => {
    if (students && students.length > 0 && !termCount) {
      setTermCount(students[0].term_count.toString());
    }
    if (students && students.length > 0 && !term) {
      if (extra && extra.term) {
        setTerm(extra.term.toString());
      }
    }
  }, [students, termCount, term, extra]);

  const submitHandeler = async () => {
    if (!students || students.length === 0) {
      toast.error("Please select at least one student!");
      return;
    }

    const invalidStudent = students.find(
      (student) =>
        !isValidTotalMark(student.total) || !isValidGainedMark(student.gained)
    );

    if (invalidStudent) {
      toast.error(
        `Please enter valid total and gained marks for ${invalidStudent.name}.`
      );
      return;
    }

    const normalizedStudents = students.map((student) => ({
      ...student,
      total: parseFloat(student.total),
      gained: parseFloat(student.gained),
    }));

    setLoading(true);
    const check = await editSchoolPerformance(term, year, normalizedStudents);
    if (check === true) {
      navigate("/admin/school-performance/view");
    }
    setLoading(false);
  };

  const confirmDeleteHandeler = async (id) => {
    if (deletingId === id) {
      return;
    }

    setDeletingId(id);
    const check = await deleteSchoolPerformance(id);
    if (check === true) {
      setStudents((currentStudents) =>
        currentStudents
          ? currentStudents.filter((student) => student.id !== id)
          : currentStudents
      );
    }
    setDeletingId(null);
  };

  const deleteHandeler = (student) => {
    if (deletingId) {
      return;
    }

    modals.openConfirmModal({
      title: "Delete this school performance",
      centered: true,
      children: (
        <Text size="md">
          Are you sure you want to delete the school performance entry for{" "}
          <b>{student.name}</b>? This action is destructive and you can not undo
          it.
        </Text>
      ),
      labels: {
        confirm: "Delete Entry",
        cancel: "No don't delete it",
      },
      confirmProps: { color: "red", loading: deletingId === student.id },
      onCancel: () => {},
      onConfirm: () => confirmDeleteHandeler(student.id),
    });
  };

  const termOptions = termCount ? getTermOptions(parseInt(termCount)) : [];

  return (
    <Container>
      <Card bg="white" text="dark" className={`crd shadow`}>
        <Card.Body>
          <Row>
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="grade" className="d-block">
                  Class
                </label>
              </div>
              <Form.Select
                onChange={(e) => {
                  setBatch("");
                  setShift("");
                  setGrade(e.target.value);
                }}
              >
                <option value={""}>Select Class</option>
                {grades &&
                  grades.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
              </Form.Select>
            </Col>
            {grade !== "" ? (
              <Col md={3} className="py-3">
                <div className="d-flex justify-content-between align-items-center pb-2">
                  <label htmlFor="shift" className="d-block">
                    Shift
                  </label>
                </div>
                <Form.Select
                  onChange={(e) => {
                    setBatch("");
                    setShift(e.target.value);
                  }}
                >
                  <option value={""}>Select Shift</option>
                  {grades
                    .filter((grd) => grd.id === parseInt(grade))[0]
                    .shifts.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                </Form.Select>
              </Col>
            ) : (
              <></>
            )}
            {grade !== "" && shift !== "" ? (
              <Col md={3} className="py-3">
                <div className="d-flex justify-content-between align-items-center pb-2">
                  <label htmlFor="batch" className="d-block">
                    Batch
                  </label>
                </div>
                <Form.Select onChange={(e) => setBatch(e.target.value)}>
                  <option value={""}>Select Batch</option>
                  {grades
                    .filter((grd) => grd.id === parseInt(grade))[0]
                    .batches.filter((bt) => bt.shiftId === parseInt(shift))
                    .map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                </Form.Select>
              </Col>
            ) : (
              <></>
            )}
          </Row>
          <Row>
            <Col md={4} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="termCount" className="d-block">
                  Term Count
                </label>
              </div>
              <Form.Select
                onChange={(e) => {
                  setTermCount(e.target.value);
                  setTerm("");
                }}
                id="termCount"
                name="termCount"
                value={termCount}
              >
                <option value={""}>Select Term Count</option>
                {getTermCountOptions().map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="term" className="d-block">
                  Term Type
                </label>
              </div>
              <Form.Select
                onChange={(e) => {
                  setTerm(e.target.value);
                }}
                id="term"
                name="term"
                value={term}
              >
                <option value={""}>Select Term Type</option>
                {termOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="year" className="d-block">
                  Year
                </label>
              </div>
              <Form.Select
                onChange={(e) => {
                  setYear(e.target.value);
                }}
                id="year"
                name="year"
                value={year}
              >
                {years.map((item, i) => (
                  <option key={i} value={`${item}`}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Col>
            {grade !== "" && shift !== "" && batch !== "" && term !== "" ? (
              <Col
                md={3}
                className="d-flex jsutify-content-end align-items-end py-3"
              >
                <Button
                  onClick={selectHandeler}
                  variant="primary"
                  type="submit"
                  className="btn_primary"
                >
                  Select
                </Button>
              </Col>
            ) : (
              <></>
            )}
          </Row>

          {students && students.length > 0 ? (
            <div className={styles.tbl_wrapper}>
              <h5 className="pt-4 pb-2">
                {getTermLabel(parseInt(termCount), parseInt(term))} - {year}
              </h5>
              <Table bordered responsive striped>
                <thead>
                  <tr>
                    <th>UID</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Total</th>
                    <th>Gained</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => (
                    <tr key={student.studentId}>
                      <td>{student.uid}</td>
                      <td>{student.name}</td>
                      <td>{student.phone}</td>
                      <td>
                        <InputGroup>
                          <Form.Control
                            type="number"
                            value={student.total}
                            onChange={(e) =>
                              updateStudentField(
                                student.studentId,
                                "total",
                                e.target.value
                              )
                            }
                          />
                        </InputGroup>
                      </td>
                      <td>
                        <InputGroup>
                          <Form.Control
                            type="number"
                            value={student.gained}
                            onChange={(e) =>
                              updateStudentField(
                                student.studentId,
                                "gained",
                                e.target.value
                              )
                            }
                          />
                        </InputGroup>
                      </td>
                      <td className="text-center">
                        <span
                          className={styles.delete_btn}
                          onClick={() => deleteHandeler(student)}
                        >
                          <BiTrash />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Button
                onClick={submitHandeler}
                variant="primary"
                className="btn_primary"
                disabled={loading}
              >
                {loading ? "Loading..." : "Submit"}
              </Button>
            </div>
          ) : students && students.length === 0 ? (
            <span className="fs-5 d-block text-center pt-4">
              No School Performance Found
            </span>
          ) : (
            <></>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  extra: state.schoolPerformance.extra,
});

export default connect(mapStateToProps, {
  deleteSchoolPerformance,
  editSchoolPerformance,
  getBatchSchoolPerformanceResult,
})(EditSchoolPerformanceForm);
